import { FC, InputEventHandler, InputHTMLAttributes, KeyboardEvent, Ref, useState } from 'react';

import { search } from '@purpur/library/icon/assets/search';

import { KeyboardKey } from '@telialt/common-constants/src/keyboard-keys';

import { useInputContext } from '../../providers/InputContextProvider';
import { InputWithSuggestionsTestID } from '../../test-ids';
import { CustomSearchIcon, InputBackdrop, InputContainer, InputWrapper, SearchInput } from './InputField.styles';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    ref?: Ref<HTMLInputElement>;
};

export const InputField: FC<Props> = ({ ref, onFocus, onBlur, ...rest }) => {
    const {
        variant,
        label,
        name,
        placeholder,
        required,
        requiredEmptyErrorMessage,
        requiredNotSelectedErrorMessage,
        disabled,
        readonly,
        inputValue,
        setInputValue,
        suggestions,
        focusedSuggestionIndex,
        setFocusedSuggestionIndex,
        selectedSuggestion,
        onSelect,
        onShowSuggestions,
        isSuggestionsVisible,
        showValidIcon,
    } = useInputContext();

    const [isTouched, setIsTouched] = useState(false);

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyboardKey.Enter && suggestions.length > 0) {
            event.preventDefault();
            const suggestionToSelect = suggestions[focusedSuggestionIndex || 0];
            onSelect(suggestionToSelect);
            setFocusedSuggestionIndex(null);

            return;
        }
        if (event.key === KeyboardKey.ArrowUp) {
            event.preventDefault();
            if (focusedSuggestionIndex === null || focusedSuggestionIndex === 0) {
                setFocusedSuggestionIndex(suggestions.length - 1);
            } else {
                setFocusedSuggestionIndex(focusedSuggestionIndex - 1);
            }

            return;
        }
        if (event.key === KeyboardKey.ArrowDown) {
            event.preventDefault();
            if (focusedSuggestionIndex === null || focusedSuggestionIndex === suggestions.length - 1) {
                setFocusedSuggestionIndex(0);
            } else {
                setFocusedSuggestionIndex(focusedSuggestionIndex + 1);
            }
        }
    };

    const handleInputChange: InputEventHandler<HTMLInputElement> = (event) => {
        setInputValue(event.currentTarget.value);
        onSelect(null);
        onShowSuggestions();
    };

    const handleBlur = (e) => {
        setIsTouched(true);
        onBlur?.(e);
    };

    const handleClear = () => {
        setInputValue('');
        onSelect(null);
    };

    const hasNotSelectedError =
        required && isTouched && inputValue.length > 0 && !selectedSuggestion && !isSuggestionsVisible;

    const hasValidIcon = !!selectedSuggestion && showValidIcon;

    return (
        <InputWrapper $variant={variant} $isSuggestionsVisible={isSuggestionsVisible}>
            {variant === 'gray' && <InputBackdrop />}
            <InputContainer>
                <SearchInput
                    {...rest}
                    type="search"
                    autoComplete="off"
                    inputRef={ref}
                    name={`${name}Search`}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    requiredErrorMessage={requiredEmptyErrorMessage}
                    errorMessage={hasNotSelectedError ? requiredNotSelectedErrorMessage : undefined}
                    disabled={disabled}
                    readOnly={readonly}
                    onKeyDown={handleInputKeyDown}
                    onInput={handleInputChange}
                    onFocus={onFocus}
                    onBlur={handleBlur}
                    value={inputValue}
                    dataTest={InputWithSuggestionsTestID.FIELD}
                    showValidIcon={hasValidIcon}
                    startAdornment={<CustomSearchIcon key="1" svg={search} size={variant === 'gray' ? 'lg' : 'xs'} />}
                    onClear={handleClear}
                    clearButtonAriaLabel="išvalyti"
                />
                <input type="hidden" name={name} value={selectedSuggestion?.value || ''} />
            </InputContainer>
        </InputWrapper>
    );
};
